import React from 'react';
import { 
  SITE_CONFIG, 
  BANNER_CONFIG, 
  LOGO_CONFIG, 
  SEO_META, 
  OPEN_GRAPH_META,
  STRUCTURED_DATA_CONFIG,
  getCurrentUrl,
  generateMetaTags
} from '../constants/metadata';
import { Profession } from '../types';

interface SEOStructuredDataProps {
  isEmbedMode?: boolean;
  selectedProfession?: Profession;
  selectedOverseasTerritory?: any;
  selectedGrade?: any;
  selectedEchelon?: any;
}

export default function SEOStructuredData({ 
  isEmbedMode = false, 
  selectedProfession,
  selectedOverseasTerritory,
  selectedGrade,
  selectedEchelon
}: SEOStructuredDataProps) {
  const baseUrl = SITE_CONFIG.BASE_URL;
  const currentUrl = getCurrentUrl();
  const canonicalUrl = typeof window !== 'undefined'
    ? `${baseUrl}${window.location.pathname.replace(/\/+$/, '') || '/'}`
    : baseUrl;
  
  // Generate dynamic meta tags
  const metaTags = generateMetaTags('default', selectedProfession, selectedOverseasTerritory, selectedGrade, selectedEchelon);
  
  // Schema.org - WebPage
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": metaTags.title,
    "description": metaTags.description,
    "url": currentUrl,
    "image": BANNER_CONFIG.IMAGE_URL,
    "inLanguage": OPEN_GRAPH_META.LOCALE.replace('_', '-'),
    "isPartOf": {
      "@type": "WebSite",
      "name": SITE_CONFIG.SITE_NAME,
      "url": baseUrl
    },
    "about": {
      "@type": "Thing",
      "name": selectedProfession ? `Salaire ${selectedProfession.name}` : "Salaire Infirmier IDE",
      "description": selectedProfession ? 
        `Calcul du salaire des ${selectedProfession.name.toLowerCase()} dans la fonction publique hospitalière française` :
        "Calcul du salaire des infirmiers diplômés d'état dans la fonction publique hospitalière française"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": selectedProfession ? selectedProfession.name : "Infirmiers diplômés d'état"
    },
    "publisher": {
      "@type": "Organization",
      "name": STRUCTURED_DATA_CONFIG.ORGANIZATION.NAME,
      "url": STRUCTURED_DATA_CONFIG.ORGANIZATION.URL,
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_CONFIG.IMAGE_URL
      }
    }
  };

  // Schema.org - SoftwareApplication
  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": selectedProfession ? 
      `Simulateur de Salaire ${selectedProfession.name}` : 
      STRUCTURED_DATA_CONFIG.SOFTWARE_APPLICATION.NAME,
    "description": selectedProfession ? 
      `Outil interactif pour estimer le salaire net des ${selectedProfession.name.toLowerCase()} dans la fonction publique hospitalière, incluant grades, échelons et primes. Calcul basé sur la grille indiciaire officielle 2025.` :
      STRUCTURED_DATA_CONFIG.SOFTWARE_APPLICATION.DESCRIPTION,
    "applicationCategory": STRUCTURED_DATA_CONFIG.SOFTWARE_APPLICATION.APPLICATION_CATEGORY,
    "operatingSystem": STRUCTURED_DATA_CONFIG.SOFTWARE_APPLICATION.OPERATING_SYSTEM,
    "url": baseUrl,
    "screenshot": BANNER_CONFIG.IMAGE_URL,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "description": selectedProfession ? 
        `Simulateur gratuit de salaire pour ${selectedProfession.name.toLowerCase()}` :
        "Simulateur gratuit de salaire pour infirmiers IDE"
    },
    "featureList": STRUCTURED_DATA_CONFIG.SOFTWARE_APPLICATION.FEATURE_LIST,
    "softwareVersion": STRUCTURED_DATA_CONFIG.SOFTWARE_APPLICATION.SOFTWARE_VERSION,
    "datePublished": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "creator": {
      "@type": "Organization",
      "name": STRUCTURED_DATA_CONFIG.ORGANIZATION.NAME,
      "url": STRUCTURED_DATA_CONFIG.ORGANIZATION.URL
    },
    "provider": {
      "@type": "Organization",
      "name": STRUCTURED_DATA_CONFIG.ORGANIZATION.NAME,
      "url": STRUCTURED_DATA_CONFIG.ORGANIZATION.URL
    }
  };

  // Schema.org - HowTo
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": selectedProfession ? 
      `Comment calculer votre salaire de ${selectedProfession.name.toLowerCase()}` :
      STRUCTURED_DATA_CONFIG.HOW_TO.NAME,
    "description": selectedProfession ?
      `Suivez ces étapes pour obtenir une estimation précise de votre salaire de ${selectedProfession.name.toLowerCase()} dans la fonction publique hospitalière.` :
      STRUCTURED_DATA_CONFIG.HOW_TO.DESCRIPTION,
    "image": BANNER_CONFIG.IMAGE_URL,
    "totalTime": STRUCTURED_DATA_CONFIG.HOW_TO.TOTAL_TIME,
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "EUR",
      "value": "0"
    },
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Informations sur votre grade et échelon"
      },
      {
        "@type": "HowToSupply", 
        "name": "Détails sur vos primes et indemnités"
      }
    ],
    "step": STRUCTURED_DATA_CONFIG.HOW_TO.STEPS.map((step, index) => ({
      "@type": "HowToStep",
      "name": step.name,
      "text": step.text,
      "url": `${baseUrl}${step.url}`,
      "image": `${baseUrl}/3.png`
    }))
  };

  // Schema.org - FAQPage
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": STRUCTURED_DATA_CONFIG.FAQ.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Schema.org - BreadcrumbList
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": (() => {
      const breadcrumbs = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": baseUrl
        }
      ];
      
      let position = 2;
      
      if (selectedProfession) {
        breadcrumbs.push({
          "@type": "ListItem",
          "position": position++,
          "name": selectedProfession.name,
          "item": `${baseUrl}${selectedProfession.path || ''}`
        });
      }
      
      if (selectedOverseasTerritory && selectedOverseasTerritory.id) {
        breadcrumbs.push({
          "@type": "ListItem",
          "position": position++,
          "name": selectedOverseasTerritory.name,
          "item": currentUrl
        });
      }
      
      if (selectedGrade && selectedEchelon) {
        breadcrumbs.push({
          "@type": "ListItem",
          "position": position++,
          "name": `${selectedGrade.nom}, Échelon ${selectedEchelon.echelon}`,
          "item": currentUrl
        });
      }
      
      return breadcrumbs;
    })()
  };

  // Ne pas afficher en mode embed pour éviter la duplication
  if (isEmbedMode) {
    return null;
  }

  return (
    <>
      {/* Canonical Tag */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr-FR" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      <meta name="robots" content="index,follow" />
      
      {/* Dynamic Title and Meta Description */}
      <title>{metaTags.title}</title>
      <meta name="description" content={metaTags.description} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={metaTags.openGraph.title} />
      <meta property="og:description" content={metaTags.openGraph.description} />
      <meta property="og:url" content={canonicalUrl} />

      {/* Twitter Meta Tags */}
      <meta name="twitter:title" content={metaTags.twitter.title} />
      <meta name="twitter:description" content={metaTags.twitter.description} />
      <meta name="twitter:url" content={canonicalUrl} />

      {/* WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(webPageSchema)
        }}
      />
      
      {/* SoftwareApplication Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(softwareApplicationSchema)
        }}
      />
      
      {/* HowTo Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema)
        }}
      />
      
      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema)
        }}
      />
      
      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema)
        }}
      />
    </>
  );
}